import { useCallback, useMemo } from 'react';
import { Button } from '@dsg-tech/homefield-react';
import { breakpointSelector } from 'store/selectors/commonSelector';
import { useAppSelector } from 'store/store';
import { getIsValidHost } from 'utility/common-utils';
import { getUserToken } from 'utility/auth-utils';
import { cartEditProductEvent } from '../../analytics/analytics-events';
import { makeApiRequest } from '../../utility/utils';

type SelectedSkuType = {
  partNumber: string;
};

type CallbackArgumentType = {
  state: 'SUCCESS' | 'FAILURE';
  sku?: string;
};

type UpdateProductPropsType = {
  onClose: () => void;
  ecode: string;
  selectedSku: SelectedSkuType;
  skuDefaultValue: string;
  callback: (param: CallbackArgumentType) => void;
  isProductOOS: boolean;
};

const UpdateProduct = ({
  onClose,
  ecode,
  selectedSku,
  skuDefaultValue,
  callback,
  isProductOOS,
}: UpdateProductPropsType) => {
  const viewPortBreakpoint = useAppSelector(breakpointSelector);

  const host = window.location ? window.location.host : '';
  const getFavoritesUrl = useCallback(() => {
    let returnUrl;

    if (getIsValidHost() || host.includes('prod-')) {
      returnUrl = `https://${window.location.host}/api/v1/carts`;
    } else {
      returnUrl = `/cart-api/api/v1/carts`;
    }

    return returnUrl;
  }, [host]);

  const parsedSku = useMemo(() => {
    if (!selectedSku) return null;

    return selectedSku.partNumber;
  }, [selectedSku]);

  const updateProduct = useCallback(async () => {
    await makeApiRequest({
      endpoint: `${getFavoritesUrl()}/contents/${skuDefaultValue}/update`,
      method: 'PUT',
      data: {
        sku: parsedSku,
      },
      getUserToken,
      onClose,
      callback,
      analyticsEventCallback: cartEditProductEvent,
      eventData: { EditedProductID: ecode, EditedProductSKU: skuDefaultValue },
    });
  }, [parsedSku, skuDefaultValue, onClose, callback, ecode]);

  const shouldBeDisabled = useMemo(() => {
    // if the product is out of stock, disable the button
    if (isProductOOS) return true;
    // if the sku is not valid (invalid combination or not selected anything), disable the button
    if (!parsedSku) return true;
    // if the sku is the same as the default sku, disable the button
    return parsedSku === skuDefaultValue;
  }, [parsedSku, skuDefaultValue, isProductOOS]);

  return (
    <div
      className={`quickview-button-edit-product-container ${
        viewPortBreakpoint === 'mobile' ? 'edit-mobile' : ''
      }`}
      data-testid="quickViewModal-update-buttons">
      <Button variant="secondary" label="Cancel" text="Cancel" onClick={onClose} />
      <Button
        variant="primary"
        label="Update item"
        text="Update item"
        onClick={updateProduct}
        disabled={shouldBeDisabled}
      />
    </div>
  );
};

export default UpdateProduct;
